import { useState } from "react";
import Icon from "../../../components/base/Icon";
import { toastError } from "../../../components/base/Toast";
import { useHub } from "../../../core/Global";
import SignupActions from "./SignupActions";
import "./SignupBuilder.css";
import "./SignupJobSources.css";


interface SignupJobSourcesProps {
    /** Called once every source has been created. */
    onDone: () => void
    /** Move on without adding anything — nothing in the setup wizard is compulsory. */
    onSkip: () => void
    /** Leave the whole setup and open the console. */
    onSkipSetup: () => void
}

// Matches Enumerate.JobSourceTypes on the backend.
const SOURCE_COMPANY = 1;
const SOURCE_ADVERTISEMENT = 2;

interface SourceBox {
    type: number
    title: string
    icon: "building" | "bullhorn"
    /** What this kind of source is, in one line. */
    hint: string
    placeholder: string
    suggestions: string[]
}

const BOXES: SourceBox[] = [
    {
        type: SOURCE_COMPANY,
        title: "Companies",
        icon: "building",
        hint: "Businesses that send you work — dispatchers, partners, insurers.",
        placeholder: "Add a company",
        suggestions: ["Direct Customer", "Insurance", "Dealership", "Property Manager"],
    },
    {
        type: SOURCE_ADVERTISEMENT,
        title: "Advertisements",
        icon: "bullhorn",
        hint: "Where the call came from — the channels you advertise on.",
        placeholder: "Add an advertisement",
        suggestions: ["Google Ads", "Yelp", "Facebook", "Website", "Referral"],
    },
];


// Second step of setting the account up: where the work comes from.
//
// Two boxes rather than one list with a picker beside it: a source is either a company that
// sends work or a channel it was advertised on, they are counted and paid for differently, and
// keeping them apart on screen is what makes that obvious.
export default function SignupJobSources(props: SignupJobSourcesProps) {

    const hub = useHub();

    const [sources, sourcesSet] = useState<{ [type: number]: string[] }>({
        [SOURCE_COMPANY]: [],
        [SOURCE_ADVERTISEMENT]: []
    });
    const [draft, draftSet] = useState<{ [type: number]: string }>({});
    const [working, workingSet] = useState<boolean>(false);

    const listOf = (type: number) => sources[type] ?? [];

    const add = (type: number, name: string) => {
        const clean = name.trim();
        if (clean.length < 2) return;

        // A company and an advertisement may share a name, so only the box being added to is
        // checked for a duplicate.
        if (listOf(type).some(s => s.toLowerCase() === clean.toLowerCase())) {
            draftSet({ ...draft, [type]: "" });
            return;
        }

        sourcesSet({ ...sources, [type]: [...listOf(type), clean] });
        draftSet({ ...draft, [type]: "" });
    };

    const remove = (type: number, name: string) => {
        sourcesSet({ ...sources, [type]: listOf(type).filter(s => s !== name) });
    };

    const total = listOf(SOURCE_COMPANY).length + listOf(SOURCE_ADVERTISEMENT).length;

    const save = () => {
        const pending = BOXES.flatMap(b => listOf(b.type).map(name => ({ type: b.type, name })));

        // Nothing added means nothing to create, so Continue is simply the way past this step.
        if (pending.length === 0) {
            props.onSkip();
            return;
        }

        workingSet(true);

        var left = pending.length;
        var failed = false;

        pending.forEach(item => {
            hub.Post("/base/jobsource", { name: item.name, type: item.type, rate: "0", fee: "0" }, res => {
                if (!res.success) failed = true;
                left--;
                if (left > 0) return;

                workingSet(false);
                if (failed) toastError("Some sources could not be created. Please check them and try again.");
                else props.onDone();
            });
        });
    };

    return (
        <div className="signup-jobsources">

            <div className="jobsources-boxes">
                {
                    BOXES.map(box => {

                        const list = listOf(box.type);

                        // Whatever has not been added yet. Adding the first one used to take the
                        // rest of the strip with it, which is the opposite of what a list of
                        // suggestions is for.
                        const available = box.suggestions.filter(s =>
                            !list.some(x => x.toLowerCase() === s.toLowerCase()));

                        return (
                            <div key={box.type} className="signup-box jobsources-box">

                                <div className="signup-box-head">
                                    <Icon icon={box.icon} type="solid" size={14} className="text-gray-500 me-2" />
                                    <span className="signup-box-name">{box.title}</span>
                                    {
                                        list.length > 0 &&
                                        <span className="signup-box-count">{list.length}</span>
                                    }
                                </div>

                                <div className="jobsources-hint">{box.hint}</div>

                                <div className="signup-add">
                                    <input className="form-control form-control-sm form-inputs"
                                        placeholder={box.placeholder}
                                        value={draft[box.type] ?? ""}
                                        onChange={e => draftSet({ ...draft, [box.type]: e.target.value })}
                                        onKeyDown={e => {
                                            if (e.key !== "Enter") return;
                                            // The step sits inside a form; Enter adds a source
                                            // rather than submitting it.
                                            e.preventDefault();
                                            add(box.type, draft[box.type] ?? "");
                                        }} />
                                    <button type="button" className="btn btn-sm btn-icon btn-light-primary"
                                        onClick={() => add(box.type, draft[box.type] ?? "")}>
                                        <Icon icon="plus" type="solid" size={12} />
                                    </button>
                                </div>

                                {
                                    list.length > 0 &&
                                    <div className="signup-chips">
                                        {
                                            list.map(name =>
                                                <span key={name} className="signup-chip">
                                                    {name}
                                                    <button type="button" onClick={() => remove(box.type, name)}>
                                                        <Icon icon="xmark" type="solid" size={10} />
                                                    </button>
                                                </span>
                                            )
                                        }
                                    </div>
                                }

                                {
                                    available.length > 0 &&
                                    <div className="jobsources-suggestions">
                                        {
                                            available.map(s =>
                                                <button type="button" key={s} className="jobsources-suggestion"
                                                    onClick={() => add(box.type, s)}>
                                                    <Icon icon="plus" type="solid" size={10} className="me-1" />
                                                    {s}
                                                </button>
                                            )
                                        }
                                    </div>
                                }

                            </div>
                        );
                    })
                }
            </div>

            <SignupActions>
                <button type="button" className="btn btn-light"
                    onClick={props.onSkipSetup} disabled={working}>
                    Skip setup &amp; go to console
                </button>

                <div className="text-gray-500 fs-8 ms-auto d-none d-md-block">
                    {total > 0 && `${total} ${total === 1 ? "source" : "sources"}`}
                </div>

                <button type="button" className={"btn btn-lg btn-primary min-w-200px" + (total > 0 ? "" : " ms-auto")}
                    disabled={working} onClick={save}>
                    {
                        working ?
                            <>
                                <span className="indicator-label me-2">Please Wait</span>
                                <Icon icon="spinner-third" spin />
                            </>
                            :
                            <>
                                <span className="indicator-label me-2">Continue</span>
                                <Icon icon="chevron-right" />
                            </>
                    }
                </button>
            </SignupActions>

        </div>
    );
}
